// ============================================================
// ARCHIVO: src/components/TabActividadesUpload.js
// ============================================================
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Stack, TextField, MenuItem,
  Button, Chip, Alert, CircularProgress, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, IconButton, Tooltip,
} from "@mui/material";
import { FiRefreshCw, FiSearch, FiExternalLink, FiX } from "react-icons/fi";
import { getAuth } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";

const COLORS = {
  bg: "#f5f0ff", white: "#ffffff", whiteSoft: "#fff8ff",
  subtle: "#e6dfef", red: "#ff3333", redDark: "#cc0000",
  textMain: "#2d233a", textMuted: "#6c6478",
};

async function authedFetch(url, opts = {}) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("No autenticado");
  const token = await user.getIdToken();
  const res = await fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
      "x-firebase-uid": user.uid,
    },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
  return res.json();
}

function safeStr(v) { return v === null || v === undefined ? "" : String(v); }
function fullName(r) { return `${safeStr(r?.nombre)} ${safeStr(r?.apellido_pat)}`.trim(); }

function DiasBadge({ dias }) {
  if (dias === null || dias === undefined) return <span style={{ color: "#aaa" }}>—</span>;
  const n = Number(dias);
  const color = n > 7 ? "#b00020" : n > 3 ? "#e65100" : "#1b5e20";
  const bg    = n > 7 ? "#ffebee" : n > 3 ? "#fff3e0" : "#e8f5e9";
  return (
    <Box sx={{ display:"inline-block", px:1, py:0.3, borderRadius:1, backgroundColor:bg, color, fontWeight:800, fontSize:12 }}>
      {n > 7 ? `🔴 ${n} días` : n > 3 ? `🟡 ${n} días` : `🟢 ${n} días`}
    </Box>
  );
}

export default function TabActividadesUpload() {
  const [programa, setPrograma] = useState("");
  const [estado, setEstado]     = useState("");
  const [matricula, setMatricula] = useState("");
  const [page, setPage]         = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const [programas, setProgramas] = useState([]);
  const [estadosScope, setEstadosScope] = useState([]);
  const [groupCodesScope, setGroupCodesScope] = useState([]);
  const [esAdmin, setEsAdmin] = useState(false);

  // Panel lateral de detalle
  const [selected, setSelected] = useState(null);
  const [score, setScore]       = useState("");
  const [comentario, setComentario] = useState("");
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState("");

  // Cargar programas disponibles para el filtro
  useEffect(() => {
    authedFetch(`${API_BASE}/progreso/admin/evaluar/programas`)
    .then(d => {
        setProgramas(Array.isArray(d?.programs) ? d.programs : []);
        setEstadosScope(Array.isArray(d?.estados) ? d.estados : []);
        setGroupCodesScope(Array.isArray(d?.groupCodes) ? d.groupCodes : []);
        setEsAdmin(!d?.estados || d.estados === null);
    })
    .catch(() => setProgramas([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE}/progreso/admin/evaluar/uploads`);
      url.searchParams.set("page", page + 1);
      url.searchParams.set("limit", rowsPerPage);
      if (programa) url.searchParams.set("programa", programa);
      if (estado) {
        if (groupCodesScope.length > 0) {
            url.searchParams.set("group_code", estado);
        } else {
            url.searchParams.set("estado", estado);
        }
        }
      if (matricula.trim()) url.searchParams.set("matricula", matricula.trim());
      const res = await authedFetch(url.toString());
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [programa, estado, matricula, page, rowsPerPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelectRow = (row) => {
    setSelected(row);
    setScore(safeStr(row.calificacion ?? ""));
    setComentario(safeStr(row.review_note ?? ""));
    setSaveMsg("");
  };

  const handleSave = async () => {
    if (!selected) return;
    if (score === "" || score === null) { setSaveMsg("⚠️ Ingresa una calificación"); return; }
    setSaving(true);
    setSaveMsg("");
    try {
      await authedFetch(
        `${API_BASE}/progreso/admin/evaluar/uploads/${selected.activity_id}/calificar`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: selected.user_id,
            score: Number(score),
            comentario: comentario || null,
          }),
        }
      );
      setSaveMsg("✅ Guardado correctamente");
      await fetchData();
      // Actualizar el selected con los nuevos datos
      setSelected(prev => ({ ...prev, calificacion: Number(score), review_note: comentario }));
    } catch (e) {
      setSaveMsg(`❌ ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const uploads = data?.uploads || [];
  const stats   = data?.stats   || {};
  const total   = data?.pagination?.total || 0;

  return (
    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
      {/* ── PANEL PRINCIPAL ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.subtle}`, mb: 2 }}>

          {/* Stats */}
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
            {[
              { label: "Total pendientes", value: stats.pendientes ?? "—", color: "#e65100" },
              { label: "Calificadas",       value: stats.calificadas ?? "—", color: "#1b5e20" },
              { label: "Total usuarios",    value: stats.total_usuarios ?? "—", color: COLORS.textMain },
              { label: "Total uploads",     value: stats.total_uploads ?? "—", color: COLORS.textMuted },
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{ px: 2, py: 1.2, borderRadius: 2, border: `1px solid ${COLORS.subtle}`, minWidth: 130 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>{s.label}</Typography>
              </Paper>
            ))}
          </Stack>

          {/* Filtros */}
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.2} alignItems="center" sx={{ mb: 1.5 }}>
            {/* Programa */}
            <TextField select size="small" label="Programa" value={programa}
              onChange={e => { setPrograma(e.target.value); setPage(0); }}
              sx={{ width: 260, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todos los programas</MenuItem>
              {programas.map(p => <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>)}
            </TextField>

            {/* Estado / Grupo Alen — SIEMPRE VISIBLE, con contenido dinámico según rol */}
            <TextField select size="small" 
              label={groupCodesScope.length > 0 ? "Grupo Alen" : "Estado"}
              value={estado}
              onChange={e => { setEstado(e.target.value); setPage(0); }}
              sx={{ width: 200, backgroundColor: COLORS.white }}>
              <MenuItem value="">
                {groupCodesScope.length > 0 ? "Todos mis grupos" : "Todos mis estados"}
              </MenuItem>
              {esAdmin
                ? ["Aguascalientes","Baja California","Baja California Sur","Campeche",
                   "Chiapas","Chihuahua","Ciudad de México","Coahuila","Colima","Durango",
                   "Estado de México","Guanajuato","Guerrero","Hidalgo","Jalisco",
                   "Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla",
                   "Querétaro","Quintana Roo","San Luis Potosí","Sinaloa","Sonora",
                   "Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán","Zacatecas",
                  ].map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)
                : groupCodesScope.length > 0
                  ? groupCodesScope.map(gc => <MenuItem key={gc} value={gc}>{gc}</MenuItem>)
                  : estadosScope.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)
              }
            </TextField>

            {/* Matrícula */}
            <TextField size="small" label="Matrícula" value={matricula}
              onChange={e => { setMatricula(e.target.value); setPage(0); }}
              sx={{ width: 160, backgroundColor: COLORS.white }}
              InputProps={{ endAdornment: <Tooltip title="Buscar"><IconButton onClick={fetchData}><FiSearch /></IconButton></Tooltip> }}
            />

            {/* Refrescar */}
            <Tooltip title="Refrescar">
              <IconButton onClick={fetchData} sx={{ border: `1px solid ${COLORS.subtle}`, borderRadius: 2, backgroundColor: COLORS.white }}>
                <FiRefreshCw />
              </IconButton>
            </Tooltip>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>}

          <Divider sx={{ mb: 1.5, borderColor: COLORS.subtle }} />

          {/* Tabla */}
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {["Aspirante", "Actividad", "Módulo / Bloque", "Documento", "Intentos", "Días sin calificar", "Calificación", "Acciones"].map(h => (
                    <TableCell key={h} sx={{ backgroundColor: COLORS.whiteSoft, fontWeight: 900, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                      <CircularProgress size={18} /><Typography sx={{ color: COLORS.textMuted }}>Cargando…</Typography>
                    </Stack>
                  </TableCell></TableRow>
                ) : uploads.length === 0 ? (
                  <TableRow><TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ color: COLORS.textMuted }}>Sin actividades pendientes.</Typography>
                  </TableCell></TableRow>
                ) : uploads.map((row, idx) => (
                  <TableRow key={idx} hover selected={selected?.activity_id === row.activity_id && selected?.user_id === row.user_id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 12 }}>{fullName(row)}</Typography>
                      <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>{safeStr(row.matricula)}</Typography>
                      <Chip label={safeStr(row.estado_usuario)} size="small" sx={{ fontSize: 10, height: 18, mt: 0.3 }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 12 }}>{safeStr(row.actividad_nombre)}</Typography>
                      <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>{safeStr(row.actividad_code)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12 }}>{safeStr(row.modulo_nombre)}</Typography>
                      <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>{safeStr(row.bloque_nombre)}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.file_url ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography sx={{ fontSize: 11, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {safeStr(row.file_name) || "Archivo"}
                          </Typography>
                          <Tooltip title="Ver documento">
                            <IconButton size="small" onClick={() => window.open(row.file_url, "_blank")}>
                              <FiExternalLink size={13} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>Sin archivo</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{safeStr(row.total_intentos) || "—"}</Typography>
                    </TableCell>
                    <TableCell><DiasBadge dias={row.dias_sin_calificar} /></TableCell>
                    <TableCell>
                      {row.calificacion !== null && row.calificacion !== undefined
                        ? <Chip label={Number(row.calificacion).toFixed(1)} size="small" sx={{ fontWeight: 900, backgroundColor: "#e8f5e9", color: "#1b5e20" }} />
                        : <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>Pendiente</Typography>}
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="contained"
                        onClick={() => handleSelectRow(row)}
                        sx={{ backgroundColor: COLORS.red, "&:hover": { backgroundColor: COLORS.redDark }, borderRadius: 2, fontWeight: 900, textTransform: "none", fontSize: 11 }}>
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div" count={total} page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Paper>
      </Box>

      {/* ── PANEL LATERAL DE DETALLE ── */}
      {selected && (
        <Paper elevation={2} sx={{
          width: 340, flexShrink: 0, borderRadius: 3, p: 2,
          border: `1px solid ${COLORS.subtle}`, backgroundColor: COLORS.whiteSoft,
          alignSelf: "flex-start", position: "sticky", top: 16,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 900, color: COLORS.textMain }}>Detalle de la actividad</Typography>
            <IconButton size="small" onClick={() => setSelected(null)}><FiX /></IconButton>
          </Stack>

          <Divider sx={{ mb: 1.5, borderColor: COLORS.subtle }} />

          {/* Info del aspirante */}
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(selected)}</Typography>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>Matrícula: {safeStr(selected.matricula)}</Typography>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>Programa: {safeStr(selected.bloque_code)}</Typography>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>Bloque actual: {safeStr(selected.bloque_nombre)}</Typography>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>Actividad: {safeStr(selected.actividad_nombre)}</Typography>
            {selected.doc_fecha && <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>Subido: {new Date(selected.doc_fecha).toLocaleString("es-MX")}</Typography>}
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>Intento actual: {safeStr(selected.total_intentos)} de {safeStr(selected.total_intentos)}</Typography>
          </Stack>

          {/* Archivo */}
          {selected.file_url && (
            <Button fullWidth variant="outlined" size="small"
              onClick={() => window.open(selected.file_url, "_blank")}
              startIcon={<FiExternalLink />}
              sx={{ mb: 1.5, borderColor: COLORS.subtle, color: COLORS.textMain, textTransform: "none", fontWeight: 700 }}>
              Ver / Descargar
            </Button>
          )}

          <Divider sx={{ mb: 1.5, borderColor: COLORS.subtle }} />

          {/* Calificación */}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Calificación</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <TextField
              size="small" type="number" label="0 - 10"
              value={score}
              onChange={e => setScore(e.target.value)}
              inputProps={{ min: 0, max: 10, step: 0.1 }}
              sx={{ width: 100, backgroundColor: COLORS.white }}
            />
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>/ 10</Typography>
          </Stack>

          {/* Comentario */}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Comentario</Typography>
          <TextField
            fullWidth multiline rows={3} size="small"
            placeholder="Escribe un comentario (opcional si apruebas, obligatorio si rechazas)..."
            value={comentario}
            onChange={e => setComentario(e.target.value)}
            sx={{ mb: 1.5, backgroundColor: COLORS.white }}
          />

          {saveMsg && (
            <Alert severity={saveMsg.startsWith("✅") ? "success" : "warning"} sx={{ mb: 1.5, borderRadius: 2, py: 0 }}>
              {saveMsg}
            </Alert>
          )}

          <Button fullWidth variant="contained" disabled={saving} onClick={handleSave}
            sx={{ backgroundColor: COLORS.red, "&:hover": { backgroundColor: COLORS.redDark }, borderRadius: 2, fontWeight: 900, textTransform: "none" }}>
            {saving ? "Guardando…" : "Guardar calificación"}
          </Button>

          <Typography sx={{ color: COLORS.textMuted, fontSize: 11, mt: 1, textAlign: "center" }}>
            Al guardar se recalculará el progreso del módulo automáticamente.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}